import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Briefcase, Clock, CheckCircle2, Trash2, ChevronDown, X, ArrowLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WBL_TYPES = [
  { value: 'job_shadow', label: 'Job Shadow', desc: 'Observe a professional for a day or more' },
  { value: 'internship', label: 'Internship', desc: 'Structured work experience, usually paid' },
  { value: 'apprenticeship', label: 'Apprenticeship', desc: 'Long-term earn & learn program' },
  { value: 'co_op', label: 'Co-op', desc: 'Alternating study and work terms' },
  { value: 'mentorship', label: 'Mentorship', desc: 'Regular sessions with an industry mentor' },
  { value: 'service_learning', label: 'Service Learning', desc: 'Community service tied to learning outcomes' },
  { value: 'simulated_workplace', label: 'Simulated Workplace', desc: 'School-based business or clinic' },
  { value: 'other', label: 'Other', desc: 'Any other work-based experience' },
];

function AddExperienceForm({ onClose, onAdded }) {
  const { getAuthHeader } = useAuth();
  const [form, setForm] = useState({ type: 'job_shadow', employer: '', role: '', hours: '', start_date: '', end_date: '', supervisor: '', description: '' });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employer || !form.hours) { toast.error('Employer and hours are required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/nc/wbl`, { ...form, hours: parseFloat(form.hours) }, getAuthHeader());
      toast.success('Experience logged');
      onAdded();
      onClose();
    } catch {
      toast.error('Could not save experience');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-[#0a1729]/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        className="w-full max-w-lg bg-[#0b1b30] border border-cyan-400/20 rounded-2xl p-6 space-y-4"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold">Log WBL Experience</h3>
          <button onClick={onClose} className="text-slate-300 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs text-slate-300 mb-1 block">Experience Type</label>
            <select
              value={form.type}
              onChange={e => set('type', e.target.value)}
              className="w-full px-3 py-2 bg-white/[0.04] border border-white/[0.12] rounded-lg text-sm text-white"
            >
              {WBL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-300 mb-1 block">Employer / Organization *</label>
              <input value={form.employer} onChange={e => set('employer', e.target.value)} required
                className="w-full px-3 py-2 bg-white/[0.04] border border-white/[0.12] rounded-lg text-sm text-white placeholder-slate-500"
                placeholder="Company name" />
            </div>
            <div>
              <label className="text-xs text-slate-300 mb-1 block">Your Role / Title</label>
              <input value={form.role} onChange={e => set('role', e.target.value)}
                className="w-full px-3 py-2 bg-white/[0.04] border border-white/[0.12] rounded-lg text-sm text-white placeholder-slate-500"
                placeholder="e.g. Marketing Intern" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-300 mb-1 block">Hours *</label>
              <input type="number" step="0.5" min="0.5" value={form.hours} onChange={e => set('hours', e.target.value)} required
                className="w-full px-3 py-2 bg-white/[0.04] border border-white/[0.12] rounded-lg text-sm text-white"
                placeholder="40" />
            </div>
            <div>
              <label className="text-xs text-slate-300 mb-1 block">Start Date</label>
              <input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)}
                className="w-full px-3 py-2 bg-white/[0.04] border border-white/[0.12] rounded-lg text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-slate-300 mb-1 block">End Date</label>
              <input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)}
                className="w-full px-3 py-2 bg-white/[0.04] border border-white/[0.12] rounded-lg text-sm text-white" />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-300 mb-1 block">Supervisor Name</label>
            <input value={form.supervisor} onChange={e => set('supervisor', e.target.value)}
              className="w-full px-3 py-2 bg-white/[0.04] border border-white/[0.12] rounded-lg text-sm text-white placeholder-slate-500"
              placeholder="Supervisor's full name" />
          </div>

          <div>
            <label className="text-xs text-slate-300 mb-1 block">What did you learn / do?</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2}
              className="w-full px-3 py-2 bg-white/[0.04] border border-white/[0.12] rounded-lg text-sm text-white placeholder-slate-500 resize-none"
              placeholder="Brief description of your experience..." />
          </div>

          <button type="submit" disabled={saving}
            className="w-full py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-semibold text-sm transition-colors disabled:opacity-60"
            data-testid="wbl-submit-btn">
            {saving ? 'Saving...' : 'Log Experience'}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
}

export default function WBLTrackerPage() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    axios.get(`${API}/nc/wbl`, getAuthHeader())
      .then(r => setData(r.data))
      .catch(() => toast.error('Could not load WBL experiences'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const deleteExp = async (id) => {
    if (!window.confirm('Delete this experience?')) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/nc/wbl/${id}`, getAuthHeader());
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Could not delete');
    } finally {
      setDeleting(null);
    }
  };

  const TYPE_LABEL = Object.fromEntries(WBL_TYPES.map(t => [t.value, t.label]));
  const experiences = data?.experiences || [];
  const total = data?.total_hours || 0;
  const pct = data?.percent_complete || 0;

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto space-y-6 pb-16" data-testid="wbl-tracker-page">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-3xl border px-7 py-8 md:px-10"
          style={{ background: 'linear-gradient(135deg,#0b1f38,#0a1729)', borderColor: 'rgba(125,211,252,0.20)' }}>
          <div aria-hidden className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full blur-3xl opacity-25"
            style={{ background: 'radial-gradient(circle,#22d3ee,transparent 68%)' }} />
          <div className="relative flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-300">
                NC Career Development Plan · Section 5
              </span>
              <h1 className="font-outfit mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-white">
                Work-Based Learning
              </h1>
              <p className="mt-3 max-w-3xl text-base leading-relaxed text-slate-300/90">
                North Carolina asks every student to finish high school with real workplace hours,                 job shadowing, internships, apprenticeships, mentorships, and your counselor has to
                verify them. Log an experience here once and it counts toward your 40-hour requirement,
                fills Section 5 of your Career Development Plan, and appears in your Academic Portfolio
                without you entering it again.
              </p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="flex shrink-0 items-center gap-2 rounded-full border border-cyan-300/40 bg-cyan-400/[0.16]
                px-5 py-2.5 text-sm font-semibold text-cyan-100 hover:bg-cyan-400/[0.26] transition-colors"
              data-testid="add-wbl-btn"
            >
              <Plus className="w-4 h-4" /> Log an experience
            </button>
          </div>
          <Link to="/app/my-cdp"
            className="relative mt-6 inline-flex items-center gap-2 text-sm text-cyan-300 hover:text-cyan-200 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to my Career Development Plan
          </Link>
        </motion.div>

        {/* Progress */}
        <div className="rounded-2xl border border-white/10 bg-[#0b1b30] p-6">
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="text-sm text-slate-300">Verified hours toward your NC requirement</p>
              <p className="text-4xl font-bold text-white mt-1">{total} <span className="text-lg text-slate-300 font-normal">/ 40 hrs</span></p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-cyan-400">{pct}%</p>
              <p className="text-xs text-slate-300">NC goal</p>
            </div>
          </div>
          <div className="h-3 bg-white/[0.06] rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className={`h-full rounded-full ${pct >= 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-cyan-500' : 'bg-amber-500'}`}
            />
          </div>
          {pct >= 100 && (
            <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> 40-hour NC requirement met!
            </p>
          )}
        </div>

        {/* Experiences */}
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : experiences.length === 0 ? (
          <div className="text-center py-12 text-slate-300">
            <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium text-slate-300 mb-1">No experiences logged yet</p>
            <p className="text-sm">Job shadows, internships, apprenticeships and mentorships all count toward your 40 NC hours.</p>
          </div>
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            {experiences.map((exp, i) => (
              <motion.div
                key={exp.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="rounded-2xl border border-white/10 bg-[#0b1b30] p-5"
                data-testid="wbl-exp-card"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/30">
                        {TYPE_LABEL[exp.type] || exp.type}
                      </span>
                      {exp.verified && (
                        <span className="text-xs flex items-center gap-1 text-emerald-400">
                          <CheckCircle2 className="w-3 h-3" /> Counselor Verified
                        </span>
                      )}
                    </div>
                    <p className="text-white font-semibold text-base">{exp.employer}</p>
                    {exp.role && <p className="text-slate-300 text-sm">{exp.role}</p>}
                    <div className="flex items-center gap-3 mt-2 text-[12.5px] text-slate-300 flex-wrap">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{exp.hours} hrs</span>
                      {exp.start_date && <span>{exp.start_date} {exp.end_date && `→ ${exp.end_date}`}</span>}
                      {exp.supervisor && <span>Supervisor: {exp.supervisor}</span>}
                    </div>
                    {exp.description && <p className="text-slate-300 text-xs mt-1.5 line-clamp-2">{exp.description}</p>}
                  </div>
                  <button
                    onClick={() => deleteExp(exp.id)}
                    disabled={deleting === exp.id}
                    className="text-slate-300 hover:text-red-400 transition-colors p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showForm && <AddExperienceForm onClose={() => setShowForm(false)} onAdded={load} />}
      </AnimatePresence>
    </AppLayout>
  );
}
